import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/Input';

/**
 * Filtre de période réutilisable : 2 inputs date natifs + badges presets.
 *
 * Deux modes d'utilisation :
 *  - `<DateRangePicker />` : compose inputs + badges sur la même ligne (layout
 *    par défaut, utilisé par ActivityPage).
 *  - `<DateRangeInputs />` + `<DateRangeBadges />` séparés : permet de placer
 *    les inputs dans `PageHeader.actions` (sur la ligne du titre) et les
 *    badges en dessous (Dashboard, KPI Direction).
 */

export interface DateRange {
  from: string;
  to: string;
}

export interface DateRangePreset<K extends string = string> {
  key: K;
  label: string;
  range: () => DateRange;
}

interface BaseProps<K extends string> {
  value: DateRange;
  activePresetKey: K | null;
  presets: ReadonlyArray<DateRangePreset<K>>;
  onChange: (next: DateRange, presetKey: K | null) => void;
}

/** YYYY-MM-DD pour un `input[type=date]`. */
export function isoToDateInput(iso: string): string {
  return iso.slice(0, 10);
}

/** YYYY-MM-DD → ISO UTC 00:00 (ou 23:59:59.999 si endOfDay). */
export function dateInputToIso(date: string, endOfDay = false): string {
  const suffix = endOfDay ? 'T23:59:59.999Z' : 'T00:00:00Z';
  return `${date}${suffix}`;
}

// ---------------------------------------------------------------------------
// Sous-composant : uniquement les 2 inputs date. Modifier manuellement une
// date désactive systématiquement le preset (second param `null` cf parent).
// ---------------------------------------------------------------------------
export function DateRangeInputs({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (next: DateRange, presetKey: null) => void;
}) {
  function handleFromChange(v: string) {
    if (!v) return;
    onChange({ from: dateInputToIso(v), to: value.to }, null);
  }
  function handleToChange(v: string) {
    if (!v) return;
    onChange({ from: value.from, to: dateInputToIso(v, true) }, null);
  }
  return (
    <div className="flex items-center gap-2">
      <Input
        type="date"
        value={isoToDateInput(value.from)}
        onChange={(e) => handleFromChange(e.target.value)}
        aria-label="Date de début"
        className="w-auto"
      />
      <span className="text-xs text-text-muted">→</span>
      <Input
        type="date"
        value={isoToDateInput(value.to)}
        onChange={(e) => handleToChange(e.target.value)}
        aria-label="Date de fin"
        className="w-auto"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-composant : uniquement les badges presets. Clic sur un badge → le
// parent reçoit le couple (range généré, clé du preset actif).
// ---------------------------------------------------------------------------
export function DateRangeBadges<K extends string>({
  activePresetKey,
  presets,
  onChange,
  align = 'start',
}: {
  activePresetKey: K | null;
  presets: ReadonlyArray<DateRangePreset<K>>;
  onChange: (next: DateRange, presetKey: K) => void;
  align?: 'start' | 'end';
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2',
        align === 'end' ? 'justify-end' : 'justify-start',
      )}
    >
      {presets.map((p) => {
        const active = activePresetKey === p.key;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.range(), p.key)}
            aria-pressed={active}
            // Style aligné sur les pills "filtres actifs" de la page
            // Prospects (voir ActiveFilterPills). Actif = fond accent-50 /
            // texte accent-600. Inactif = fond gris léger, hover se teinte.
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
              active
                ? 'bg-accent-50 text-accent-600'
                : 'bg-gray-100 text-text-muted hover:bg-accent-50 hover:text-accent-600',
            )}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composant composé : inputs + badges sur la même ligne
// ---------------------------------------------------------------------------
export function DateRangePicker<K extends string>({
  value,
  activePresetKey,
  presets,
  onChange,
}: BaseProps<K>) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <DateRangeInputs value={value} onChange={onChange} />
      <DateRangeBadges activePresetKey={activePresetKey} presets={presets} onChange={onChange} />
    </div>
  );
}
